import type { Shape } from '../../types';

/**
 * ShapePicker - Visual shape selection
 *
 * Features:
 * - Four button options: Circle, Square, Star, Custom
 * - Visual shape previews
 * - Pro gating (only Circle free)
 */

interface ShapePickerProps {
  value: Shape;
  onChange: (shape: Shape) => void;
  disabled?: boolean;
  isPro: boolean;
}

/**
 * Renders a 3D ball icon using CSS gradients
 */
function Ball3DIcon(): JSX.Element {
  return (
    <div
      className="w-6 h-6 rounded-full"
      style={{
        background: `
          radial-gradient(circle at 30% 30%, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 20%),
          radial-gradient(circle at 50% 50%, #333 0%, #111 60%, #000 100%)
        `,
        boxShadow: '0 2px 4px rgba(0,0,0,0.3), inset 0 -3px 6px rgba(0,0,0,0.4)',
      }}
    />
  );
}

const SHAPES: Array<{
  value: Shape;
  label: string;
  icon: string | 'custom-ball';
  description: string;
  isPro: boolean;
}> = [
  {
    value: 'ball',
    label: '3D Ball',
    icon: 'custom-ball',
    description: '3D ball with gradient',
    isPro: false,
  },
  {
    value: 'circle',
    label: 'Circle',
    icon: '⚫',
    description: 'Classic flat circle',
    isPro: false,
  },
  {
    value: 'square',
    label: 'Square',
    icon: '⬛',
    description: 'Square shape',
    isPro: false,
  },
  {
    value: 'triangle',
    label: 'Triangle',
    icon: '▲',
    description: 'Triangle shape',
    isPro: true,
  },
  {
    value: 'star',
    label: 'Star',
    icon: '★',
    description: 'Star shape',
    isPro: true,
  },
];

export function ShapePicker({
  value,
  onChange,
  disabled = false,
  isPro,
}: ShapePickerProps): JSX.Element {
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Shape
      </label>

      <div className="flex flex-wrap gap-2">
        {SHAPES.map((shape) => {
          const isLocked = shape.isPro && !isPro;
          const isSelected = value === shape.value;

          return (
            <button
              key={shape.value}
              type="button"
              onClick={() => !isLocked && !disabled && onChange(shape.value)}
              disabled={disabled || isLocked}
              className={`
                relative w-[60px] h-[60px] rounded border-2 transition-all flex items-center justify-center flex-shrink-0
                ${isSelected ? 'border-sync-blue bg-sync-blue/10' : 'border-gray-300 bg-white'}
                ${isLocked ? 'opacity-40 cursor-not-allowed' : 'hover:border-sync-blue cursor-pointer'}
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              title={shape.label}
            >
              {shape.icon === 'custom-ball' ? <Ball3DIcon /> : <span className="text-2xl">{shape.icon}</span>}
              {isLocked && <span className="absolute top-1 right-1 text-xs pro-icon"></span>}
            </button>
          );
        })}
      </div>

      {!isPro && (
        <div className="text-xs text-gray-500 text-center pt-1">
          <span className="pro-icon inline-block mr-1"></span> Upgrade to Pro for custom shapes
        </div>
      )}
    </div>
  );
}
